import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ReportProblemButton = ({ inMenu }) => {
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [captcha, setCaptcha] = useState(''); // user response for the math captcha
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Funktion zum Abrufen eines Captchas als Rechenaufgabe
  const fetchCaptcha = async () => {
    try {
      const response = await fetch('/api/captcha');
      if (response.ok) {
        const data = await response.json(); // erwartet { question: "3+4", token: "abc123" }
        setCaptchaQuestion(data.question);
        setCaptchaToken(data.token);
      } else {
        setError('Captcha konnte nicht geladen werden.');
      }
    } catch (err) {
      setError('Captcha konnte nicht geladen werden.');
    }
  };

  // Beim Öffnen des Modals Captcha laden und alte Eingaben zurücksetzen
  useEffect(() => {
    if (showModal) {
      fetchCaptcha();
      setCaptcha('');
      setError('');
      setMessage('');
    }
  }, [showModal]);

  const handleSubmit = async () => {
    if (!description || !email || !captcha) {
      setError('Bitte füllen Sie alle Felder aus.');
      return;
    }

    try {
      const response = await fetch('/api/report-problem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description, email, captcha, captchaToken }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Dein Problem wurde gemeldet und wird zeitnah bearbeitet! HINWEIS: ist COMMUNITY Projekt, erwarte keine Echtzeit-Reaktion ;-)');
        setTimeout(() => {
          setShowModal(false);
          navigate('/');
        }, 9000); // 3 Sekunden warten, bevor auf die Hauptseite umgeleitet wird
      } else {
        setError(data.error || 'Ein Fehler ist aufgetreten.');
        // Bei Fehler wird ein neues Captcha geladen
        fetchCaptcha();
      }
    } catch (err) {
      setError('Ein Fehler ist aufgetreten.');
      fetchCaptcha();
    }
  };

  // Button-Styling: Hintergrundfarbe auf Blau
  const buttonStyle = {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    outline: 'none',
    height: '38px',
    lineHeight: '18px'
  };

  // Modal-Styling: Angelehnt an ein Login-Formular
  const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    width: '400px',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    zIndex: 1001,
    fontFamily: 'Arial, sans-serif',
    color: '#333' // Sicherstellen, dass der Text sichtbar ist
  };

  // Container style for both buttons
  const containerStyle = inMenu
    ? {
        display: 'inline-flex',
        gap: '10px',
        whiteSpace: 'nowrap',
        alignItems: 'center'
      }
    : {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        display: 'flex',
        gap: '10px',
        zIndex: 1000
      };

  // YouTube button style - inherit from buttonStyle for consistency
  const youtubeButtonStyle = {
    ...buttonStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none', // Remove underline from link
    padding: '0 20px',  // Adjust padding to match height
    height: '38px'      // Exact height match
  };

  return (
    <>
      <div style={containerStyle}>
        <button
          onClick={() => setShowModal(true)}
          className="report-problem-btn"
          style={buttonStyle}
        >
          Problem melden
        </button>
        <button
          onClick={() => window.open('https://www.youtube.com/@gerihifi/videos', '_blank')}
          style={youtubeButtonStyle}
        >
          <img
            src="youtube.svg"
            alt="YouTube"
            style={{
              width: '24px',
              height: '24px',
              filter: 'brightness(0) invert(1)', // Makes the SVG white
              display: 'block',  // Prevent any extra space
              margin: '0 auto'   // Center the icon
            }}
          />
        </button>
      </div>

      {showModal && (
        <div className="modal" style={modalStyle}>
          <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>Problem melden</h2>
          {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}
          {message && <div style={{ color: 'green', marginBottom: '10px' }}>{message}</div>}

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '5px' }}>
              Ihre E-Mail:
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '5px' }}>
              Problembeschreibung:
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="5"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>
              Captcha: {captchaQuestion ? `Löse die Aufgabe: ${captchaQuestion} = ?` : 'Lade Captcha...'}
            </div>
            <input
              type="text"
              id="captcha"
              value={captcha}
              onChange={(e) => setCaptcha(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
              placeholder="Antwort eingeben"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              onClick={() => setShowModal(false)}
              style={{
                padding: '10px 20px',
                border: 'none',
                backgroundColor: '#ccc',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Senden
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportProblemButton;